import React, { SetStateAction, Dispatch, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  COLOR_BLACK,
  COLOR_BLUE,
  COLOR_GREY,
  COLOR_BLACK_BORDER,
  COLOR_WHITE,
} from "../constants/colors";
import {
  GESTION_LYON,
  GESTION_PARIS,
  ATTESTATION_HEBERGEMENT,
  CHANGEMENT_ADRESSE,
  COURRIER_RESILIATION,
  COMPTE_RENDU_GERANCE,
  FORMALITE_DEMENAGEMENT,
  CONSOMMATION_EAU,
  DEGAT_EAU,
  TRI_DECHETS,
  BRUITS_VOISINAGE,
  CHARGES_LOCATIVES,
  REPARATIONS_LOCATIVES,
  FAIRE_GERER,
  COMPANY_LOCATION,
} from "../constants/menu";
import { Map, Phone, Email, Person } from "@mui/icons-material";
import { NavLink } from "react-router-dom";

const Wrapper = styled.div`
  background-color: ${COLOR_GREY};
  * {
    box-sizing: border-box;
  }
  @media screen and (max-width: 600px) {
    display: none;
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 95%;
  height: auto;
  margin: auto;
  font-size: 12px;
  line-height: 16, 79px;
`;

const Item = styled.a`
  color: ${COLOR_BLACK};
  cursor: pointer;
  text-decoration: none;
  font-weight: 400;
  margin: 6px 12px;
  background-color: transparent;

  &:hover {
    text-decoration: underline;
  }

  .icon {
    position: relative;
    top: 2px;
    right: 3px;
    font-family: "Material Icons";
    font-size: 12px;
  }

  b {
    color: ${COLOR_BLUE};
    font-weight: bolder;
  }
`;

const ItemLink = styled(NavLink)`
  color: ${COLOR_BLACK};
  cursor: pointer;
  text-decoration: none;
  font-weight: 400;
  margin: 6px 12px;
  background-color: transparent;

  &:hover {
    text-decoration: underline;
  }

  .icon {
    position: relative;
    top: 2px;
    right: 3px;
    font-family: "Material Icons";
    font-size: 12px;
  }

  b {
    color: ${COLOR_BLUE};
    font-weight: bolder;
  }
`;

const Document = styled.div`
  display: inline-block;
  position: relative;
  color: ${COLOR_BLACK};
  cursor: pointer;
  text-decoration: none;
  font-weight: 400;
  margin: 6px 12px;
  background-color: transparent;

  .sub-menu {
    position: absolute;
    z-index: 10;
    left: 50%;
    transform: translateX(-50%);
    visibility: hidden;
    opacity: 0;
    transition: opacity 750ms;
  }

  .arrow {
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid #222222c0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 10px;
  }

  .content {
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    box-shadow: 0px 2px 5px rgb(0 0 0 / 50%);
    border-radius: 10px;
    background-color: ${COLOR_WHITE};
    visibility: hidden;
    opacity: 0;
    a {
      display: block;
      cursor: pointer;
      font-style: normal;
      text-decoration: none;
      margin: 0;
      padding: 0 24px;
      text-align: center;
      white-space: nowrap;
      line-height: 40px;
      height: 40px;
      border-bottom: 1px solid ${COLOR_BLACK_BORDER};
      transition: all 0.3s ease;
    }
    a:last-child {
      border-bottom: none;
    }
    a:hover {
      text-decoration: underline;
    }
  }

  :hover {
    .sub-menu,
    .content {
      visibility: visible;
      opacity: 1;
    }
  }
`;

const TopMenuNavigation = ({
  topMenuHeight,
  _setTopMenuHeight,
}: {
  topMenuHeight: HTMLElement["offsetHeight"];
  _setTopMenuHeight: Dispatch<SetStateAction<HTMLElement["offsetHeight"]>>;
}) => {
  const ref =
    useRef<HTMLInputElement>() as React.MutableRefObject<HTMLInputElement>;

  const getComponentHeight = () => {
    if (ref.current) _setTopMenuHeight(ref.current.offsetHeight);
  };

  useEffect(() => {
    window.addEventListener("resize", getComponentHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper ref={ref}>
      <Content>
        <ItemLink to={"/espace-presse"}>Espace Presse</ItemLink>
        <Item href={GESTION_LYON}>Gestion locative Lyon</Item>
        <Item href={GESTION_PARIS}>Gestion locative Paris</Item>
        <Item target="_blank" href={COMPANY_LOCATION}>
          <Map className="icon" />
          64, Rue Ampère 75017 Paris
        </Item>
        <Item href="tel:0173790910">
          <Phone className="icon" />
          0173790910
        </Item>
        <Item href="mailto:gestion@immonline.fr">
          <Email className="icon" />
          gestion@immonline.fr
        </Item>
        <Document className="faq">
          Documents utiles
          <div className="sub-menu">
            <div className="arrow"></div>
            <div className="content">
              <Item href={ATTESTATION_HEBERGEMENT} target="_blank">
                Attestation d'hébergement
              </Item>
              <Item href={CHANGEMENT_ADRESSE} target="_blank">
                Changement d'adresse
              </Item>
              <Item href={COURRIER_RESILIATION} target="_blank">
                Resiliation mandat de gestion
              </Item>
              <Item href={COMPTE_RENDU_GERANCE} target="_blank">
                Explication compte rendu de gérance
              </Item>
              <Item href={FORMALITE_DEMENAGEMENT} target="_blank">
                Formalités pour déménagement
              </Item>
              <Item href={CONSOMMATION_EAU} target="_blank">
                La consommation d'eau
              </Item>
              <Item href={DEGAT_EAU} target="_blank">
                Le dégat des eaux
              </Item>
              <Item href={TRI_DECHETS} target="_blank">
                Le tri des déchets ménagers
              </Item>
              <Item href={BRUITS_VOISINAGE} target="_blank">
                Les bruits du voisinage
              </Item>
              <Item href={CHARGES_LOCATIVES} target="_blank">
                Les charges locatives
              </Item>
              <Item href={REPARATIONS_LOCATIVES} target="_blank">
                Les réparations locatives
              </Item>
              <Item href={FAIRE_GERER} target="_blank">
                Pourquoi faire gérer son bien immobilier
              </Item>
            </div>
          </div>
        </Document>
        <Item href={"/espaceclient"} className="last">
          <Person className="icon" />
          <b>Espace Clients</b>
        </Item>
      </Content>
    </Wrapper>
  );
};

export default TopMenuNavigation;
